import { useState } from 'react';

import type { DailyInspiration } from '../../application/dto/DailyInspiration';
import { CompassIcon } from '../icons';
import { resolveImagery } from '../design-system/data/imagery';
import { useTranslation } from '../design-system/data/translations';
import { getPhilosophyTranslation } from '../design-system/data/philosophyI18n';
import { useAscendLocale } from '../design-system/session/useAscendLocale';

/** Pack V2 (1870×841) — remplace le bandeau vectoriel `inspiration.svg` ; celui-ci reste défini
 * en CSS pour rollback (voir revue visuelle « intégration finale des 20 visuels »). */
const CARD_IMAGE = resolveImagery('card-inspiration-v2.png');
const CARD_IMAGE_POSITION = '35% 55%';

const CARD_BANNER = (
  <div className="thematic-banner thematic-banner--photo" aria-hidden="true">
    <img
      src={CARD_IMAGE}
      alt=""
      className="thematic-banner__image"
      style={{ objectPosition: CARD_IMAGE_POSITION }}
    />
    <div className="thematic-banner__veil" />
  </div>
);

interface DailyInspirationCardProps {
  /** `undefined` : rien à afficher — voir `loading` pour distinguer du chargement en cours. */
  readonly inspiration: DailyInspiration | undefined;
  readonly loading?: boolean;
}

/** L'emplacement Inspiration stoïcienne reste toujours rendu (voir revue visuelle V7 #6). */
export function DailyInspirationCard({ inspiration, loading = false }: DailyInspirationCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const locale = useAscendLocale();

  const translatedContent =
    inspiration && locale !== 'fr-FR'
      ? getPhilosophyTranslation(inspiration.corpusIndex, locale)
      : null;

  if (!inspiration) {
    return (
      <section className="daily-inspiration editorial-card" aria-label={t('inspiration.label')}>
        {CARD_BANNER}
        <div className="daily-inspiration__toggle">
          <CompassIcon className="daily-inspiration__icon" aria-hidden="true" />
          <span className="daily-inspiration__body">
            <span className="eyebrow">{t('inspiration.label')}</span>
            <span className="daily-inspiration__text" aria-busy={loading || undefined}>
              {loading ? t('inspiration.loading') : t('inspiration.empty')}
            </span>
          </span>
        </div>
      </section>
    );
  }

  const { content } = inspiration;
  const detailId = `inspiration-detail-${inspiration.id}`;

  return (
    <section className="daily-inspiration editorial-card" aria-label={t('inspiration.label')}>
      {CARD_BANNER}
      <button
        type="button"
        className="daily-inspiration__toggle"
        aria-expanded={expanded}
        aria-controls={detailId}
        onClick={() => setExpanded((current) => !current)}
      >
        <CompassIcon className="daily-inspiration__icon" aria-hidden="true" />
        <span className="daily-inspiration__body">
          <span className="eyebrow">{t('inspiration.label')}</span>
          <span className="daily-inspiration__text">{translatedContent?.[0] ?? content.text}</span>
          <span className="daily-inspiration__origin">{t('inspiration.label')}</span>
        </span>
      </button>
      {expanded ? (
        <div id={detailId} className="daily-inspiration__detail">
          <p className="daily-inspiration__explanation">{translatedContent?.[1] ?? content.explanation}</p>
          <p className="daily-inspiration__challenge">{translatedContent?.[2] ?? content.challenge}</p>
        </div>
      ) : null}
    </section>
  );
}
